/**
 * Навигация и обвивка на приложението (Раздел 8 от ТЗ).
 * Интерфейсът е на български, за десктоп от 1366×768 нагоре.
 */

import { AppShell, Burger, Center, Group, Loader, NavLink, Text, Button } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import type { Permission } from '@vidanalyser/shared';
import { useAuth } from './auth/AuthContext';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { LoginPage } from './pages/LoginPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { RecordingDetailPage } from './pages/RecordingDetailPage';
import { RecordingsPage } from './pages/RecordingsPage';
import { SettingsPage } from './pages/SettingsPage';
import { UploadPage } from './pages/UploadPage';
import { UsersPage } from './pages/UsersPage';

interface NavigationItem {
  label: string;
  path: string;
  permission?: Permission;
}

const NAVIGATION: NavigationItem[] = [
  { label: 'Разговори', path: '/recordings' },
  { label: 'Качване', path: '/upload', permission: 'recordings:upload' },
  { label: 'Потребители', path: '/users', permission: 'users:manage' },
  { label: 'Настройки', path: '/settings', permission: 'settings:manage' },
];

export function App() {
  const { user, isLoading, logout, can } = useAuth();
  const [opened, { toggle }] = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();

  if (isLoading) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }

  if (!user) {
    // Линкът от писмото и забравената парола се отварят без вход — човекът още
    // няма с какво да влезе.
    return (
      <Routes>
        <Route path="/set-password/:token" element={<SetPasswordPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  const visibleItems = NAVIGATION.filter((item) => !item.permission || can(item.permission));

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 220, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Text fw={600}>Анализ на разговори</Text>
          </Group>
          <Group gap="sm">
            <Text size="sm" c="dimmed">
              {user.fullName} · {roleLabel(user.role)}
            </Text>
            <Button variant="subtle" size="xs" onClick={() => void logout()}>
              Изход
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm">
        {visibleItems.map((item) => (
          <NavLink
            key={item.path}
            label={item.label}
            active={location.pathname.startsWith(item.path)}
            onClick={() => navigate(item.path)}
          />
        ))}
      </AppShell.Navbar>

      <AppShell.Main>
        <Routes>
          <Route path="/recordings" element={<RecordingsPage />} />
          <Route path="/recordings/:id" element={<RecordingDetailPage />} />
          {can('recordings:upload') && <Route path="/upload" element={<UploadPage />} />}
          {can('users:manage') && <Route path="/users" element={<UsersPage />} />}
          {can('settings:manage') && <Route path="/settings" element={<SettingsPage />} />}
          <Route path="*" element={<Navigate to="/recordings" replace />} />
        </Routes>
      </AppShell.Main>
    </AppShell>
  );
}

function roleLabel(role: string): string {
  if (role === 'ADMIN') return 'Администратор';
  if (role === 'MONITORING') return 'Мониторинг';
  return 'Ръководител';
}
